import React, { useState } from 'react'
import { Sparkles, Copy, RotateCcw, Check, Cloud } from 'lucide-react'
import { Button } from '../common'
import useCategoryStore from '../../stores/categoryStore'

// Default prompt templates for different category types
const PROMPT_TEMPLATES = {
  exterior: `Analyse this exterior photo for:
- Condition of walls/facade (cracks, damage, weathering)
- Windows and doors condition
- Paintwork or rendering condition
- Any visible structural issues
- Overall aesthetic appearance
- Recommendations for maintenance or repairs`,

  interior: `Analyse this interior room photo for:
- Condition of walls and ceilings (cracks, damp, staining)
- Flooring condition and type
- Windows condition and glazing
- Lighting fixtures
- Any visible defects or issues
- General decoration condition
- Recommendations for improvements`,

  kitchen: `Analyse this kitchen photo for:
- Condition of units and cabinets
- Worktop material and condition
- Appliances visible and their condition
- Flooring type and condition
- Wall tiles or splashback condition
- Plumbing fixtures visible
- Ventilation (extractor hood)
- Overall hygiene and cleanliness
- Recommendations for improvements`,

  bathroom: `Analyse this bathroom photo for:
- Sanitaryware condition (toilet, basin, bath/shower)
- Tiles and grouting condition
- Signs of damp or water damage
- Ventilation adequacy
- Flooring condition
- Plumbing fixtures and seals
- Overall hygiene and cleanliness
- Recommendations for improvements`,

  roof: `Analyse this roof photo for:
- Roof covering type and condition (tiles/slates/felt)
- Ridge tiles condition
- Chimney condition if visible
- Flashing around penetrations
- Guttering and downpipes
- Fascias and soffits
- Any visible damage or missing elements
- Signs of moss or algae growth
- Recommendations for maintenance`,

  garden: `Analyse this garden/outdoor photo for:
- Lawn/planting condition
- Boundary walls/fences condition
- Patio/paving condition
- Outbuildings if visible
- Drainage visible
- Access paths
- Overall maintenance level
- Recommendations for improvements`,

  utilities: `Analyse this utilities area photo for:
- Boiler type and approximate age
- Boiler condition indicators
- Gas/electric meter location
- Consumer unit/fuse box condition
- Visible pipework condition
- Any safety concerns
- Recommendations for inspection or upgrade`
}

export default function PromptEditor({ category, onClose }) {
  const updatePrompt = useCategoryStore(state => state.updatePrompt)
  const resetPrompt = useCategoryStore(state => state.resetPrompt)
  const getPrompt = useCategoryStore(state => state.getPrompt)
  const loading = useCategoryStore(state => state.loading)

  // Get current prompt data (custom if set, otherwise default)
  const promptData = getPrompt(category.code)
  const defaultPrompt = promptData?.defaultPrompt || category.aiPrompt || ''

  const [prompt, setPrompt] = useState(promptData?.prompt || defaultPrompt)
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isCustom, setIsCustom] = useState(promptData?.isCustom || false)

  const handleSave = async () => {
    try {
      await updatePrompt(category.code, prompt)
      setSaved(true)
      setIsCustom(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (error) {
      console.error('Failed to save prompt:', error)
    }
  }

  const handleUseTemplate = (template) => {
    setPrompt(template)
    setSaved(false)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleReset = async () => {
    try {
      await resetPrompt(category.code)
      setPrompt(defaultPrompt)
      setIsCustom(false)
      setSaved(false)
    } catch (error) {
      console.error('Failed to reset prompt:', error)
      // Still reset locally
      setPrompt(defaultPrompt)
      setSaved(false)
    }
  }

  // Suggest a template based on category name
  const getSuggestedTemplate = () => {
    const name = category.name.toLowerCase()
    if (name.includes('kitchen')) return PROMPT_TEMPLATES.kitchen
    if (name.includes('bathroom') || name.includes('en-suite')) return PROMPT_TEMPLATES.bathroom
    if (name.includes('roof')) return PROMPT_TEMPLATES.roof
    if (name.includes('garden')) return PROMPT_TEMPLATES.garden
    if (name.includes('utilities') || name.includes('boiler')) return PROMPT_TEMPLATES.utilities
    if (name.includes('exterior') || name.includes('front') || name.includes('rear')) return PROMPT_TEMPLATES.exterior
    return PROMPT_TEMPLATES.interior
  }

  return (
    <div className="space-y-6">
      {/* Prompt editor */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <label className="block text-sm font-medium text-slate-700">
              AI Analysis Prompt
            </label>
            {isCustom && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                <Cloud className="w-3 h-3" />
                Custom
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              disabled={!prompt}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={handleReset}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value)
            setSaved(false)
          }}
          placeholder="Enter the prompt that will be sent to ChatGPT when analysing photos in this category..."
          rows={10}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none font-mono text-sm"
        />
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-slate-500">
            Write clear instructions for the AI to follow when analysing photos in the "{category.name}" category.
          </p>
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Cloud className="w-3 h-3" />
            Synced across devices
          </span>
        </div>
      </div>

      {/* Template suggestions */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h4 className="font-medium text-slate-900 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent-500" />
            Prompt Templates
          </h4>
          <p className="text-sm text-slate-500 mt-1">
            Select a template to get started quickly
          </p>
        </div>
        <div className="p-4 space-y-2">
          <button
            onClick={() => handleUseTemplate(getSuggestedTemplate())}
            className="w-full p-3 text-left bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors"
          >
            <p className="font-medium text-primary-700">
              Suggested for "{category.name}"
            </p>
            <p className="text-sm text-primary-600 mt-1">
              Auto-detected template based on category name
            </p>
          </button>

          <div className="grid grid-cols-2 gap-2 mt-3">
            {Object.entries({
              'Exterior': PROMPT_TEMPLATES.exterior,
              'Interior Room': PROMPT_TEMPLATES.interior,
              'Kitchen': PROMPT_TEMPLATES.kitchen,
              'Bathroom': PROMPT_TEMPLATES.bathroom,
              'Roof': PROMPT_TEMPLATES.roof,
              'Garden': PROMPT_TEMPLATES.garden,
              'Utilities': PROMPT_TEMPLATES.utilities
            }).map(([name, template]) => (
              <button
                key={name}
                onClick={() => handleUseTemplate(template)}
                className="p-2 text-sm text-slate-600 hover:text-primary-700 hover:bg-primary-50 border border-slate-200 rounded-lg transition-colors text-left"
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          loading={loading}
          className="flex-1"
          icon={saved ? 'Check' : 'Save'}
        >
          {saved ? 'Saved!' : 'Save Prompt'}
        </Button>
      </div>
    </div>
  )
}
